import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import ActionButton from 'Components/ActionButton';
import { ClearIconStyled } from '..';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TEpilepsyESEtiologyType } from '../../definitions/epilepsy';

const classOptions: NonNullable<IEtiology['class']>[] = [
  'structural',
  'genetic',
  'infectional',
  'metabolism',
  'immune',
  'unknown',
];

const subClassMap: Partial<Record<NonNullable<IEtiology['class']>, string[]>> = {
  structural: [
    'cortical',
    'vascular',
    'hippocampusSclerosis',
    'perinatal',
    'transientIschaemicAttack',
    'hypoxicIschemic',
    'porencephalicCyst',
    'encephalocele',
    'hydrocephalus',
    'toxicEncephalopathy',
    'traumaticBrainInjury',
    'brainTumor',
    'brainDystrophy',
    'brainInfection',
    'other',
  ],
  genetic: ['singleGeneMutation', 'copyNumberVariation', 'otherChromosomalVariation', 'familial'],
  infectional: ['virus', 'bacteria', 'otherKnown', 'unknownShort'],
  immune: ['unknownShort', 'rasmussen', 'gad', 'nmdar', 'otherKnownAntidote'],
};

const EtiologyField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const criteria: TEpilepsyESEtiologyType[] = Array.isArray(searchFields.etiology) ? searchFields.etiology : [{}];

  const onChangeCriteria = (i: number) => (values: TOnChangeValues) => {
    const newCriteria: TEpilepsyESEtiologyType[] = structuredClone(
      Array.isArray(searchFields.etiology) && searchFields.etiology.length > 0 ? searchFields.etiology : [{}],
    );

    const fieldName = Object.keys(values)[0] as 'class' | 'subClass';
    const fieldValue = values[fieldName] as string | undefined;

    if (fieldName === 'class') {
      newCriteria[i] = { class: (fieldValue as IEtiology['class']) ?? undefined };
    } else {
      newCriteria[i] = { ...newCriteria[i], subClass: fieldValue ?? undefined };
    }

    onChange({ etiology: newCriteria });
  };

  return (
    <React.Fragment>
      {criteria.map((item, i, arr) => {
        const subClassOptions = item.class ? (subClassMap[item.class] ?? []) : [];

        return (
          <React.Fragment key={`etiologyCriteria${i}`}>
            <div
              style={{
                backgroundColor: colors.lightestGray,
                padding: '1rem',
                marginBottom: i === arr.length - 1 ? '1rem' : undefined,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                  {fm('extendedSearch.searchCriteriaTitle')}
                  {` ${i + 1}`}
                </div>
                {arr.length > 1 && (
                  <ClearIconStyled
                    onClick={() => {
                      const filtered = arr.filter((_, index) => index !== i);
                      onChange({ etiology: filtered.length > 0 ? filtered : 'noCropping' });
                    }}
                  />
                )}
              </div>
              <InputHandler
                type="Select"
                editing={true}
                name="class"
                formData={{
                  onChange: onChangeCriteria(i),
                  document: { class: item.class ?? null },
                }}
                options={classOptions}
                optionFormatter={(name: string | number): string =>
                  formatMessage({ id: `diagnosis.epilepsy.etiology.opts.${name}` })
                }
                placeholder="diagnosis.epilepsy.etiology.class"
                fittedWidth
              />
              {subClassOptions.length > 0 && (
                <React.Fragment>
                  <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                    {fm('diagnosis.epilepsy.etiology.specificClass')}
                  </div>
                  <InputHandler
                    type="Select"
                    editing={true}
                    name="subClass"
                    formData={{
                      onChange: onChangeCriteria(i),
                      document: { subClass: item.subClass ?? null },
                    }}
                    options={subClassOptions}
                    optionFormatter={(name: string | number): string =>
                      formatMessage({ id: `diagnosis.epilepsy.etiology.opts.${name}` })
                    }
                    placeholder="diagnosis.epilepsy.etiology.specificClass"
                    fittedWidth
                  />
                </React.Fragment>
              )}
            </div>
            {arr.length > 1 && i < arr.length - 1 && (
              <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.or')}</div>
            )}
          </React.Fragment>
        );
      })}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newCriteria = structuredClone(
              Array.isArray(searchFields.etiology) && searchFields.etiology.length > 0 ? searchFields.etiology : [{}],
            );
            newCriteria.push({});
            onChange({ etiology: newCriteria });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default EtiologyField;
